/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  useEffect,
  useMemo,
  useRef
} from 'react';
import {makeStyles} from '@material-ui/core';
import PageScrim from '@ion/dls/src/components/backdrop/PageScrim';
import {ClickAwayListener} from '@ion/dls/src/components/listeners/ClickAwayListener';
import {useIsPhone} from '@ion/dls/src/hooks/useMedia';
import {BasemapGalleryHeaderContent} from '@ion/dls/src/maps/layers/BasemapGalleryHeaderContent';
import IonPortal from '@ion/dls/src/render/IonPortal';
import {dlsAlt, dlsColor, dlsShape} from '@ion/dls/src/theme/theme';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {resolveBasemapLayers, translateBasemap} from '../helpers/simpleMapUtils';
import {useBasemapGallery} from '../hooks/useBasemapGallery';
import {useShowTool} from '../hooks/useShowTool';
import {basemapTitle, MapTools} from '../types/types';
import {MapViewTool} from './MapViewTool';

export const BASEMAP_GALLERY_CONTAINER_ID   = 'basemap-gallery-container';
export const BASEMAP_GALLERY_ROOT_ID        = 'basemap-gallery';
export const BASEMAP_GALLERY_CHILD_SELECTOR = '[aria-label="Base Maps"]';

// Defines the props for the BasemapGalleryTool component.
export type BasemapGalleryToolProps = {
  map: __esri.Map;
  view: __esri.MapView;
  topOffset?: number;
  rightOffset?: number;
  createOSMBasemap: Function;
  onBasemapChanged: Function;
  isOpen?: boolean;
  activeTool: MapTools;
  setActiveTool: Function;
  isOpenFromBottomSheet?: boolean;
  setSelectedTool?: Function;
  usePortal?: boolean;
  isAdminGeofence?: boolean;
  portalRightOffset?: number;
};

type StyleProps = {
  rightOffset?: number;
  isCurrent?: boolean;
  topOffset?: number;
};

// dlsColor/dlsShape must be invoked from within a function (they read the
// active theme reactively), so these are named factory functions rather
// than plain hoisted objects - still pulls the shape out of the makeStyles
// callback body, just without baking the theme tokens in at module-load
// time ...
const legacyHeaderContainer = () => ({
  width        : '100%',
  height       : 26,
  background   : dlsColor('grey10'),
  borderRadius : '4px 4px 0px 0px'
});

const currentHeaderContainer = () => ({
  display        : 'flex',
  alignItems     : 'center',
  justifyContent : 'space-between',
  width          : '100%',
  height         : 56,
  padding        : '8px 8px 8px 16px',
  boxSizing      : 'border-box',
  background     : dlsColor('white'),
  borderRadius   : `${dlsShape('borderRadius')}px ${dlsShape('borderRadius')}px 0 0`
});

export const useStyles = makeStyles(theme => ({
  mapView : ({rightOffset, isCurrent, topOffset}: StyleProps) => {
    const legacyMapView = {
      backgroundColor : dlsColor('white'),
      border          : `1px solid ${dlsColor('grey5')}`,
      boxShadow       : dlsAlt('components', 'boxShadow', 'shadow4'),
      position        : 'absolute',
      top             : 100,
      right           : rightOffset || '55px',
      width           : 370
    };
    // matches Tracking2's own current-theme basemap gallery panel look
    // (rounded corners, no border). `top` here is the caller's own measured
    // value (the trigger button's live position) rather than a fixed
    // constant - IonPortal's own `top` is pinned to 0 for this case (see
    // below) so this is the ONE place the actual offset is applied, and
    // what DevTools shows on this visible panel directly reflects it...
    const currentMapView = {
      ...legacyMapView,
      border       : 'none',
      borderRadius : dlsShape('borderRadius'),
      top          : topOffset ?? 100,
      // legacyMapView's `right` falls back on falsy 0 (not just
      // null/undefined), which would wrongly discard a genuine
      // zero-distance measurement (the button flush against the
      // viewport's right edge) - ?? only falls back on null/undefined ...
      right        : rightOffset ?? (legacyMapView.right as string | number)
    };
    return isCurrent ? currentMapView : legacyMapView;
  },
  mapViewMobile : {
    border    : `1px solid ${dlsColor('grey5')}`,
    boxShadow : dlsAlt('components', 'boxShadow', 'shadow4'),
    position  : 'absolute',
    top       : '55px',
    right     : '0px',
    minWidth  : '200px',
    margin    : '4px'
  },
  galleryBottomSheetView : {
    backgroundColor : dlsColor('white'),
    top             : '55px',
    right           : '0px',
    minWidth        : '200px',
    padding         : '8px'
  },
  galleryHeaderContainer : ({isCurrent}: StyleProps) => (
    isCurrent ? currentHeaderContainer() : legacyHeaderContainer()
  ),
  galleryHeader : {
    display       : 'flex',
    marginLeft    : 12,
    height        : '100%',
    alignItems    : 'center',
    fontFamily    : 'Roboto !important',
    fontStyle     : 'normal',
    fontSize      : '12px !important',
    lineHeight    : '16px !important',
    letterSpacing : '0.5px !important',
    color         : dlsColor('white')
  },
  basemapGalleryRoot : {
    display : 'inherit !important'
  }
}));

/**
 * The BasemapGalleryTool component provides the UI which displays the associated Icon
 * button within a DLS Map that, when clicked, initiates the corresponding
 * feature.
 *
 * This MapViewTool represents the "Maps Tool" DLS feature:
 * https://www.figma.com/file/srjlHrDuM90sCoaOwpjaDU/01_DLS_Components?node-id=1526%3A7422
 *
 * ```typescript
 *
 *   <BasemapGalleryTool map={map} view={view} onBasemapChanged={function() {}} />
 *
 * ```
 */
export const BasemapGalleryTool = ({
  map,
  view,
  topOffset,
  rightOffset,
  createOSMBasemap,
  onBasemapChanged,
  activeTool,
  setActiveTool,
  setSelectedTool,
  isOpen = false,
  usePortal = false,
  isOpenFromBottomSheet = false,
  isAdminGeofence = false,
  portalRightOffset
}: BasemapGalleryToolProps) => {
  const {isCurrent} = useThemeContext();
  // scoped to isAdminGeofence so this doesn't change this widely-shared
  // component's look for its other callers (e.g. the mobile bottom sheet)
  // under current theme - only Admin Geofences' own panel picks up
  // Tracking2's current-theme styling here...
  const useCurrentStyle     = isCurrent && isAdminGeofence;
  const classes             = useStyles({rightOffset, isCurrent: useCurrentStyle, topOffset});
  const isPhone             = useIsPhone();
  const basemapGalleryRef   = useBasemapGallery({view, createOSMBasemap});
  const initialSelectionRef = useRef(null);
  const basemapWatchRef     = useRef(null);
  const childRef            = useRef(null);
  const [show, toggleShow, handleClickOutside, setShow] = useShowTool({
    ref          : childRef,
    initialState : isOpen,
    toolId       : MapTools.basemapGallery,
    activeTool,
    setActiveTool
  });

  // forces the panel open for a bottom-sheet caller (SimpleMapControlsBottomSheet
  // passes this as a constant `true`) - guarded so a caller that never uses the
  // bottom-sheet flow (isOpenFromBottomSheet stays false, e.g. GeofenceWidgetCluster)
  // doesn't have this fire `setShow(false)` on every mount, which was clobbering
  // useShowTool's own activeTool-driven state in the same commit ...
  useEffect(() => {
    if (isOpenFromBottomSheet) {
      setShow(true);
    }
  }, [isOpenFromBottomSheet]);

  const monitorBasemapGallery = usePortal && !isOpenFromBottomSheet;
  const basemapGalleryElement = monitorBasemapGallery ? document.getElementById(BASEMAP_GALLERY_ROOT_ID) : null;
  const baseMapsChild = monitorBasemapGallery
    ? basemapGalleryElement?.querySelector?.(BASEMAP_GALLERY_CHILD_SELECTOR) : null;

  useEffect(() => {
    if (basemapGalleryElement) {
      basemapGalleryElement.style.setProperty('max-width', '40px', 'important');
      basemapGalleryElement.style.setProperty('min-width', '40px', 'important');
      basemapGalleryElement.style.setProperty('display', 'inherit', 'important');
      basemapGalleryElement.style.setProperty('min-height', '40px', 'important');
      basemapGalleryElement.style.setProperty('max-height', '40px', 'important');
      basemapGalleryElement.style.setProperty('overflow', 'hidden', 'important');
      basemapGalleryElement.classList.add(classes.basemapGalleryRoot);
    }
    if (baseMapsChild) {
      baseMapsChild.remove();
    }
  }, [basemapGalleryElement, baseMapsChild]);

  function onBasemapUpdate(newValue, oldValue) {
    if (newValue !== oldValue) {
      resolveBasemapLayers(map);
      onBasemapChanged(newValue);
      setSelectedTool?.(null);
      // Give the gallery time to display before managing the initial
      // gallery selection.
      setTimeout(manageInitialSelection, 150);
    }
  };

  /**
   * The initial selection in the gallery is not always indicated by the ESRI
   * basemap gallery widget. This function ensures that the initial selection
   * will be indicated in the widget.
   *
   * The process for determining the initial basemap selection is as follows:
   *
   * - Are any basemap gallery entries selected in the widget?
   *   - If not:
   *     - Get the current map basemap.
   *     - Is the current map basemap's entry in the gallery's DOM yet?
   *       - If so, make sure the basemap's entry displays as selected in the
   *         gallery, and save a reference to this entry (used below).
   *       - If not, wait and try again.
   *   - If so, and if more than one gallery entry is selected, it means that
   *     an entry other than the initial entry has been selected by the user.
   *     Clear the selection of the initial entry; from now on the gallery
   *     widget ensures that only one entry will ever be selected.
   *
   */
  function manageInitialSelection() {
    const selectedClass = 'esri-basemap-gallery__item--selected';
    const elSelected = document.querySelectorAll(`.${selectedClass}`);
    if (!elSelected.length && basemapGalleryRef?.current) {
      const currentBasemap = basemapGalleryRef.current.activeBasemap.id;
      const elCurrentBasemap = document.querySelector(
        `[title="${basemapTitle[currentBasemap]}"]`
      );
      if (elCurrentBasemap) {
        elCurrentBasemap.classList?.add(selectedClass);
        initialSelectionRef.current = elCurrentBasemap;
      } else {
        setTimeout(manageInitialSelection, 150);
      }
    } else if (elSelected.length > 1) {
      initialSelectionRef.current?.classList?.remove(selectedClass);
    }
  };

  useEffect(() => {
    basemapWatchRef.current = map.watch('basemap.id', onBasemapUpdate);
    return function() {
      basemapWatchRef.current?.remove('basemap.id', onBasemapUpdate);
    };
  }, [basemapWatchRef]);

  useEffect(() => {
    if (basemapGalleryRef?.current) {
      basemapGalleryRef.current.container = BASEMAP_GALLERY_CONTAINER_ID;
      basemapGalleryRef.current.visible = show;
      if (show) {
        // Give the gallery time to display before managing the initial
        // gallery selection.
        setTimeout(manageInitialSelection, 150);
        translateBasemap();
      }
    }
  }, [show, basemapGalleryRef]);

  const BaseMapComponent = useMemo(() => (
    <div ref={childRef} style={{display: show ? 'inherit' : 'none'}}
      className={
        isOpenFromBottomSheet ? classes.galleryBottomSheetView : isPhone ? classes.mapViewMobile : classes.mapView
      } >
      <div className={classes.galleryHeaderContainer}>
        <BasemapGalleryHeaderContent isCurrent={useCurrentStyle}
          onClose={toggleShow}
          legacyLabelClassName={classes.galleryHeader} />
      </div>
      <div id={BASEMAP_GALLERY_CONTAINER_ID} />
    </div>
  // toggleShow is stable (memoized in useShowTool) unless show/toolId/
  // setActiveTool change, so including it here is cheap and correct -
  // unlike `classes`, which would look like it should be here too (it's
  // what visually carries a later topOffset re-measurement) but doesn't
  // actually change reference between renders in this MUI version, so it
  // was dropped rather than left in as a no-op dependency ...
  ), [show, isPhone, isOpenFromBottomSheet, basemapGalleryRef, useCurrentStyle, toggleShow]);

  if (isOpenFromBottomSheet) {
    return BaseMapComponent;
  }

  const component: any = (
    usePortal ? (
      // for the admin-geofence/current-theme case, `top` is pinned to 0
      // here so the panel's own mapView `top` (above) is the sole place
      // the measured offset applies, rather than splitting it across both
      // this wrapper and the panel - every other caller keeps its original
      // behavior of applying `topOffset` at this level ...
      <IonPortal id='basemap-gallery-component'
        top={useCurrentStyle ? 0 : topOffset}
        right={portalRightOffset}
        show={show}>
        {/* the current theme's admin geofence map styles panel dims the page
          * behind it, as the tracking map's panels do - a click on the dimmed
          * page closes it, as an outside click always has here ... */}
        {useCurrentStyle && show && <PageScrim onClick={toggleShow} />}
        {BaseMapComponent}
      </IonPortal>
    ) : (
      BaseMapComponent
    )
  );

  return (
    <>
      <MapViewTool isSelected={show}
        onClick={toggleShow}
        isAdminGeofence={isAdminGeofence} />
      <ClickAwayListener onClickAway={handleClickOutside} mountListener>
        {component}
      </ClickAwayListener>
    </>
  );
};

export default memo(BasemapGalleryTool);
